import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Settings, ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw, Edit, Check, X, Save, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';

const STORAGE_KEY = 'employee-datagrid-v1';
const PRESETS_KEY = 'employee-datagrid-presets-v1';

// Every column that can be shown. `path` is used by inline edit to PATCH the
// employee doc via `/api/admin/employees/{id}/field`. Columns without `path`
// are read-only (e.g. derived fields like `department` which is an array join).
const ALL_COLUMNS = [
  { key: 'name',              label: 'Name',              get: e => e.personal?.full_name, path: 'personal.full_name' },
  { key: 'employee_id',       label: 'Emp ID',            get: e => e.employee_id || e.employment?.employee_id, path: 'employment.employee_id' },
  { key: 'work_email',        label: 'Work Email',        get: e => e.work_email, path: 'work_email', inputType: 'email' },
  { key: 'personal_email',    label: 'Personal Email',    get: e => e.personal?.personal_email, path: 'personal.personal_email', inputType: 'email' },
  { key: 'phone',             label: 'Phone / Mobile',    get: e => e.personal?.mobile_number || e.personal?.mobile || e.personal?.phone, path: 'personal.mobile_number' },
  { key: 'designation',       label: 'Designation',       get: e => e.designation || e.employment?.designation, path: 'employment.designation' },
  { key: 'designation_level', label: 'Level',             get: e => e.designation_level || e.employment?.designation_level, path: 'employment.designation_level' },
  { key: 'department',        label: 'Department',        get: e => (Array.isArray(e.departments) ? e.departments.join(', ') : e.departments) || (Array.isArray(e.employment?.departments) ? e.employment.departments.join(', ') : e.employment?.departments) },
  { key: 'status',            label: 'Status',            get: e => e.employment?.status || (e.is_active === false ? 'Inactive' : 'Active'), path: 'employment.status' },
  { key: 'employment_type',   label: 'Employment Type',   get: e => e.employment?.employment_type, path: 'employment.employment_type' },
  { key: 'start_date',        label: 'Start Date',        get: e => e.employment?.start_date, path: 'employment.start_date', type: 'date', inputType: 'date' },
  { key: 'first_working_day', label: 'First Working Day', get: e => e.employment?.first_working_day || e.first_working_day, path: 'employment.first_working_day', type: 'date', inputType: 'date' },
  { key: 'last_working_day',  label: 'Last Working Day',  get: e => e.employment?.last_working_day || e.employment?.last_day, path: 'employment.last_working_day', type: 'date', inputType: 'date' },
  { key: 'ctc',               label: 'CTC (₹)',           get: e => e.employment?.ctc, type: 'currency' },
  { key: 'gender',            label: 'Gender',            get: e => e.gender || e.personal?.gender, path: 'personal.gender' },
  { key: 'dob',               label: 'DOB',               get: e => e.personal?.date_of_birth, path: 'personal.date_of_birth', type: 'date', inputType: 'date' },
  { key: 'aadhar',            label: 'Aadhar',            get: e => e.personal?.aadhar_number || e.identity?.aadhar_number, path: 'identity.aadhar_number' },
  { key: 'pan',               label: 'PAN',               get: e => e.personal?.pan_number || e.identity?.pan_number, path: 'identity.pan_number' },
  { key: 'address',           label: 'Address',           get: e => e.personal?.address || e.personal?.permanent_address, path: 'personal.address' },
  { key: 'local_address',     label: 'Local Address',     get: e => e.personal?.local_address, path: 'personal.local_address' },
  { key: 'guardian_name',     label: 'Local Guardian',    get: e => e.personal?.local_guardian_name, path: 'personal.local_guardian_name' },
  { key: 'guardian_phone',    label: 'Guardian Phone',    get: e => e.personal?.local_guardian_contact, path: 'personal.local_guardian_contact' },
  { key: 'emergency_name',    label: 'Emergency Contact', get: e => e.emergency?.name, path: 'emergency.name' },
  { key: 'emergency_phone',   label: 'Emergency Phone',   get: e => e.emergency?.phone, path: 'emergency.phone' },
  { key: 'bank_account',      label: 'Bank A/C',          get: e => e.bank?.account_number || e.bank_details?.account_number, path: 'bank_details.account_number' },
  { key: 'bank_name',         label: 'Bank Name',         get: e => e.bank?.bank_name || e.bank_details?.bank_name, path: 'bank_details.bank_name' },
  { key: 'ifsc',              label: 'IFSC',              get: e => e.bank?.ifsc || e.bank_details?.ifsc_code || e.bank_details?.ifsc, path: 'bank_details.ifsc_code' },
  { key: 'leave_pl',          label: 'Leaves — Paid',     get: e => e.leave_balance?.pl, path: 'leave_balance.pl', type: 'number', inputType: 'number' },
  { key: 'leave_sl',          label: 'Leaves — Sick',     get: e => e.leave_balance?.sl, path: 'leave_balance.sl', type: 'number', inputType: 'number' },
  { key: 'leave_cl',          label: 'Leaves — Casual',   get: e => e.leave_balance?.cl || e.leave_balance?.casual, path: 'leave_balance.cl', type: 'number', inputType: 'number' },
  { key: 'leave_wfh',         label: 'WFH',               get: e => e.leave_balance?.wfh, path: 'leave_balance.wfh', type: 'number', inputType: 'number' },
  { key: 'leave_ol',          label: 'Optional Leaves',   get: e => e.leave_balance?.ol, path: 'leave_balance.ol', type: 'number', inputType: 'number' },
  { key: 'profile_completed', label: 'Profile Complete',  get: e => e.profile_completed ? 'Yes' : 'No' },
  { key: 'last_login',        label: 'Last Login',        get: e => e.last_login, type: 'date' },
  { key: 'created_at',        label: 'Added On',          get: e => e.created_at, type: 'date' },
];

const DEFAULT_VISIBLE = new Set(['name','employee_id','work_email','phone','designation','designation_level','department','status','first_working_day','ctc','aadhar','pan','bank_account','ifsc']);

// Built-in presets — always available as one-tap starting points.
const BUILT_IN_PRESETS = [
  { name: 'HR view',          columns: ['name','employee_id','work_email','phone','designation','department','status','employment_type','first_working_day','last_working_day','profile_completed'] },
  { name: 'Payroll view',     columns: ['name','employee_id','pan','aadhar','bank_account','bank_name','ifsc','ctc','employment_type','first_working_day'] },
  { name: 'Onboarding',       columns: ['name','work_email','personal_email','phone','address','emergency_name','emergency_phone','guardian_name','guardian_phone','profile_completed','first_working_day'] },
  { name: 'Leaves & Balance', columns: ['name','department','designation','leave_pl','leave_sl','leave_cl','leave_wfh','leave_ol'] },
];

function loadPrefs() {
  try { const r = localStorage.getItem(STORAGE_KEY); if (r) return JSON.parse(r); } catch {}
  return { order: ALL_COLUMNS.map(c => c.key), visible: [...DEFAULT_VISIBLE] };
}
function savePrefs(p) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch {} }
function loadUserPresets() {
  try { const r = localStorage.getItem(PRESETS_KEY); if (r) return JSON.parse(r); } catch {}
  return [];
}
function saveUserPresets(list) { try { localStorage.setItem(PRESETS_KEY, JSON.stringify(list)); } catch {} }

function formatCell(value, type) {
  if (value === undefined || value === null || value === '') return null;
  if (type === 'date') { try { const d = new Date(value); return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return String(value); } }
  if (type === 'currency') {
    if (typeof value === 'object') {
      const min = value.min ?? value.amount ?? value.value; const max = value.max;
      if (min && max && min !== max) return `₹${Number(min).toLocaleString('en-IN')} – ₹${Number(max).toLocaleString('en-IN')}`;
      if (min) return `₹${Number(min).toLocaleString('en-IN')}`;
      return '—';
    }
    const n = Number(value); return isNaN(n) ? String(value) : `₹${n.toLocaleString('en-IN')}`;
  }
  if (type === 'number') return String(value);
  if (typeof value === 'object') { try { return JSON.stringify(value); } catch { return '—'; } }
  return String(value);
}

// Inline editable cell — click to edit, Enter to save, Esc to cancel.
function EditableCell({ emp, col, initialValue, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => (initialValue == null ? '' : String(initialValue)));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const missing = initialValue === undefined || initialValue === null || initialValue === '';
  const displayed = missing ? '—' : formatCell(initialValue, col.type);

  const commit = async () => {
    if (saving) return;
    if (String(draft) === String(initialValue ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await adminApi.patch(`/api/admin/employees/${emp._id}/field`, { path: col.path, value: draft });
      onSaved(res.data.value);
      toast.success('Saved');
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  const cancel = () => { setDraft(initialValue == null ? '' : String(initialValue)); setEditing(false); };

  if (!col.path) {
    // Read-only column
    return <span className={missing ? 'text-red-400' : 'text-neutral-800'} title={missing ? 'Read-only column' : String(initialValue)}>{displayed}</span>;
  }
  if (editing) {
    return (
      <div className="flex items-center gap-1 min-w-[180px]" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type={col.inputType || 'text'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
          disabled={saving}
          className="flex-1 px-1.5 py-0.5 border border-blue-400 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
          data-testid={`grid-input-${emp._id}-${col.key}`}
        />
        <button onClick={commit} disabled={saving} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`grid-save-${emp._id}-${col.key}`}><Check className="w-3.5 h-3.5" /></button>
        <button onClick={cancel} disabled={saving} className="p-1 text-neutral-500 hover:bg-neutral-100 rounded"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className={`text-left w-full block ${missing ? 'text-red-400 italic' : 'text-neutral-800'} hover:underline decoration-dotted underline-offset-2`}
      title={missing ? 'Click to fill' : 'Click to edit'}
      data-testid={`grid-cell-btn-${emp._id}-${col.key}`}
    >
      {displayed}
    </button>
  );
}

export default function EmployeeDataGrid({ employees: incoming, onEdit }) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [showSettings, setShowSettings] = useState(false);
  const [userPresets, setUserPresets] = useState(loadUserPresets);
  const [employees, setEmployees] = useState(incoming);
  useEffect(() => { setEmployees(incoming); }, [incoming]);

  const columns = useMemo(() => {
    const byKey = Object.fromEntries(ALL_COLUMNS.map(c => [c.key, c]));
    return prefs.order.map(k => byKey[k]).filter(Boolean);
  }, [prefs.order]);
  const visibleColumns = useMemo(() => columns.filter(c => prefs.visible.includes(c.key)), [columns, prefs.visible]);

  const update = (next) => { setPrefs(next); savePrefs(next); };
  const toggleColumn = (key) => update({ ...prefs, visible: prefs.visible.includes(key) ? prefs.visible.filter(k => k !== key) : [...prefs.visible, key] });
  const moveColumn = (key, dir) => {
    const idx = prefs.order.indexOf(key); if (idx < 0) return;
    const next = [...prefs.order]; const target = idx + dir; if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    update({ ...prefs, order: next });
  };
  const resetLayout = () => update({ order: ALL_COLUMNS.map(c => c.key), visible: [...DEFAULT_VISIBLE] });
  const applyPreset = (columnKeys) => update({ ...prefs, visible: columnKeys.filter(k => ALL_COLUMNS.some(c => c.key === k)) });
  const saveNamedPreset = () => {
    const name = window.prompt('Save current column layout as preset — name it:', '');
    if (!name || !name.trim()) return;
    const cleanName = name.trim();
    const next = [...userPresets.filter(p => p.name !== cleanName), { name: cleanName, columns: [...prefs.visible] }];
    setUserPresets(next); saveUserPresets(next);
    toast.success(`Preset "${cleanName}" saved`);
  };
  const deletePreset = (name) => {
    if (!window.confirm(`Delete preset "${name}"?`)) return;
    const next = userPresets.filter(p => p.name !== name);
    setUserPresets(next); saveUserPresets(next);
  };

  // Apply an inline edit to the local employees state so the cell re-renders
  // without a full refetch.
  const setEmployeeField = (empId, path, value) => {
    setEmployees(prev => prev.map(e => {
      if (e._id !== empId) return e;
      const clone = JSON.parse(JSON.stringify(e));
      const parts = path.split('.'); let cursor = clone;
      for (let i = 0; i < parts.length - 1; i++) { cursor[parts[i]] = cursor[parts[i]] || {}; cursor = cursor[parts[i]]; }
      cursor[parts[parts.length - 1]] = value;
      return clone;
    }));
  };

  const allPresets = [...BUILT_IN_PRESETS.map(p => ({ ...p, builtIn: true })), ...userPresets];

  return (
    <div className="soft-card overflow-hidden" data-testid="employee-datagrid">
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-white/40 bg-white/40 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs text-neutral-500">
            <span className="font-semibold text-neutral-700">{employees.length}</span> employees ·
            {' '}<span className="font-semibold text-neutral-700">{visibleColumns.length}</span> / {ALL_COLUMNS.length} columns ·
            {' '}<span className="text-red-500">click any cell to edit</span>
          </p>
          {/* Preset chips */}
          {allPresets.map(p => (
            <button
              key={p.name}
              onClick={() => applyPreset(p.columns)}
              className="group inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-white/80 border border-white/60 rounded-full hover:bg-blue-50 hover:border-blue-300"
              data-testid={`preset-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
              title={p.builtIn ? `Built-in preset · ${p.columns.length} cols` : `Custom preset · ${p.columns.length} cols`}
            >
              {p.name}
              {!p.builtIn && (
                <span onClick={(e) => { e.stopPropagation(); deletePreset(p.name); }} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700">
                  <Trash2 className="w-3 h-3" />
                </span>
              )}
            </button>
          ))}
          <button onClick={saveNamedPreset} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full hover:bg-emerald-100" data-testid="preset-save">
            <Plus className="w-3 h-3" /> Save as preset
          </button>
        </div>
        <div className="relative">
          <button onClick={() => setShowSettings(v => !v)} className="px-3 py-1.5 border border-white/50 rounded-lg text-sm text-neutral-700 hover:bg-white/70 flex items-center gap-1.5" data-testid="employee-grid-settings-toggle">
            <Settings className="w-3.5 h-3.5" /> Customize columns
          </button>
          {showSettings && (
            <div className="absolute right-0 top-full mt-2 w-72 max-h-[70vh] overflow-y-auto bg-white border border-neutral-200 rounded-xl shadow-2xl z-50 p-2" data-testid="employee-grid-settings">
              <div className="flex items-center justify-between px-2 py-1.5 border-b border-neutral-100 mb-1">
                <span className="text-xs font-semibold text-neutral-700">Columns · drag order with arrows</span>
                <button onClick={resetLayout} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="grid-reset-layout"><RotateCcw className="w-3 h-3" /> Reset</button>
              </div>
              {columns.map((c, i) => {
                const on = prefs.visible.includes(c.key);
                return (
                  <div key={c.key} className="flex items-center gap-1 px-1.5 py-1 hover:bg-neutral-50 rounded">
                    <button onClick={() => toggleColumn(c.key)} className="p-1 hover:bg-neutral-100 rounded" data-testid={`grid-toggle-${c.key}`}>
                      {on ? <Eye className="w-3.5 h-3.5 text-emerald-600" /> : <EyeOff className="w-3.5 h-3.5 text-neutral-400" />}
                    </button>
                    <span className={`flex-1 text-sm ${on ? 'text-neutral-800' : 'text-neutral-400 line-through'}`}>{c.label}</span>
                    <button onClick={() => moveColumn(c.key, -1)} disabled={i === 0} className="p-1 hover:bg-neutral-100 rounded disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => moveColumn(c.key, +1)} disabled={i === columns.length - 1} className="p-1 hover:bg-neutral-100 rounded disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-auto max-h-[75vh] relative">
        <table className="min-w-max text-sm border-collapse">
          <thead className="sticky top-0 z-20">
            <tr className="bg-white/95 backdrop-blur border-b border-neutral-200">
              {visibleColumns.map((c, i) => (
                <th key={c.key} className={`px-3 py-2 text-left text-xs font-semibold text-neutral-600 whitespace-nowrap border-r border-neutral-100 ${i === 0 ? 'sticky left-0 bg-white z-10 shadow-[2px_0_0_rgba(0,0,0,0.03)]' : ''}`}>{c.label}</th>
              ))}
              <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-600 whitespace-nowrap sticky right-0 bg-white z-10 shadow-[-2px_0_0_rgba(0,0,0,0.03)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + 1} className="px-6 py-12 text-center text-neutral-500">No employees found.</td></tr>
            ) : employees.map(emp => (
              <tr key={emp._id} className="hover:bg-blue-50/40 border-b border-neutral-100" data-testid={`grid-row-${emp._id}`}>
                {visibleColumns.map((c, i) => {
                  const raw = c.get(emp);
                  const missing = raw === undefined || raw === null || raw === '';
                  return (
                    <td key={c.key} className={`px-3 py-2 whitespace-nowrap border-r border-neutral-100 ${missing ? 'bg-red-50' : ''} ${i === 0 ? 'sticky left-0 z-[5] font-medium ' + (missing ? 'bg-red-50' : 'bg-white') : ''}`} data-testid={`grid-cell-${emp._id}-${c.key}`}>
                      <EditableCell
                        emp={emp}
                        col={c}
                        initialValue={raw}
                        onSaved={(newVal) => { if (c.path) setEmployeeField(emp._id, c.path, newVal); }}
                      />
                    </td>
                  );
                })}
                <td className="px-3 py-2 whitespace-nowrap sticky right-0 bg-white z-[5] shadow-[-2px_0_0_rgba(0,0,0,0.03)]">
                  <button onClick={() => onEdit && onEdit(emp)} className="px-2 py-1 border border-neutral-200 rounded-lg text-xs text-neutral-700 hover:bg-neutral-50 flex items-center gap-1" data-testid={`grid-edit-${emp._id}`}>
                    <Edit className="w-3 h-3" /> Full edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
